import React, { useId } from 'react';
import { getMediafilePresentationKind, MediafileItem, MediafileItemSkeleton, MediafilePoster } from '@entities/media';
import IconRefresh from '@zvuk-b2b/react-uikit/ui/icons/RefreshCw';
import IconTrash2 from '@zvuk-b2b/react-uikit/ui/icons/Trash2';
import FileUtils from 'application/utils/FileUtils';
import { getMediaPreviewKind } from 'application/utils/MediaPreviewUtils';
import MediafilePosterPreview from 'application/components/MediafilePosterPreview';
import { DEFAULT_IMAGE_DURATION } from '../../consts';
import { TEXTS } from '../../texts';
import type { SelectVideoMediafilePreviewItemProps } from './types';

const VideoStreamPreviewItem: React.FC<SelectVideoMediafilePreviewItemProps> = ({
  file,
  className,
  leading,
  onRetry,
  onRemoveUpload,
}) => {
  const fallbackId = useId();
  if (!file)
    return (
      <MediafileItemSkeleton
        leading="checkbox"
        className={className}
      />
    );

  const id = file.id ?? fallbackId;
  const title = file.title ?? '';
  const kind = file.uploadState
    ? (getMediaPreviewKind({ extension: title.split('.').pop() }) ?? 'video')
    : getMediafilePresentationKind(file);

  if (file.uploadState) {
    const state = file.uploadState === 'uploading' ? 'uploading' : 'upload-error';
    let statusMessage = TEXTS.UPLOADING;
    if (file.uploadState === 'upload-error') statusMessage = TEXTS.UPLOAD_ERROR;
    if (file.uploadState === 'refresh-error') statusMessage = TEXTS.UPLOAD_REFRESH_ERROR;
    return (
      <MediafileItem
        id={id}
        state={state}
        kind={kind}
        title={title}
        className={className}
        leading={leading}
        poster={
          state === 'uploading' ? (
            <MediafilePoster
              id={id}
              state="uploading"
              kind={kind}
              durationLabel="00:00:10"
            />
          ) : (
            <MediafilePoster
              id={id}
              state="upload-error"
              kind={kind}
            />
          )
        }
        statusMessage={statusMessage}
        actions={
          state === 'upload-error'
            ? [
                {
                  id: 'retry',
                  kind: 'button',
                  label: file.uploadState === 'refresh-error' ? TEXTS.RETRY_LIBRARY_REFRESH : TEXTS.RETRY_UPLOAD,
                  icon: <IconRefresh />,
                  isDisabled: !onRetry,
                  onClick: () => onRetry?.(id),
                },
                {
                  id: 'remove',
                  kind: 'button',
                  label: TEXTS.REMOVE_UPLOAD,
                  icon: <IconTrash2 />,
                  isDisabled: !onRemoveUpload,
                  onClick: () => onRemoveUpload?.(id),
                },
              ]
            : undefined
        }
      />
    );
  }

  const original = FileUtils.getOriginalMediafileSource(file.sourcefiles);
  const resource = FileUtils.getSourcefileByUrl(file.sourcefiles, file.uri);
  const previewKind = getMediaPreviewKind({ type: file.type, extension: resource?.extension });
  return (
    <MediafileItem
      id={id}
      state="ready"
      kind={kind}
      title={title}
      leading={leading}
      className={className}
      poster={
        <MediafilePosterPreview
          id={id}
          kind={kind}
          title={title}
          thumbnail={file.previewUrl}
          duration={file.duration || file.playDuration || DEFAULT_IMAGE_DURATION}
          extension={original?.extension}
          url={file.uri ?? undefined}
          isVideo={previewKind ? previewKind === 'video' : undefined}
        />
      }
    />
  );
};

export default VideoStreamPreviewItem;
